import { useMutation as useReactQueryMutation } from "@tanstack/react-query";
import { useMutation, useQuery } from "convex/react";
import { ChevronLeftIcon, PlusIcon } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { Button } from "../../components/ui/button";
import {
	Card,
	CardContent,
	CardHeader,
	CardTitle,
} from "../../components/ui/card";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "../../components/ui/table";
import AddPreRegisteredUserDialog from "./AddPreRegisteredUserDialog";
import AddTenantDomainDialog from "./AddTenantDomainDialog";
import FirmNoticeFilingCard from "./FirmNoticeFilingCard";
import FirmOnboardingFeeCard from "./FirmOnboardingFeeCard";
import MemberDetailSheet, { type MemberDetail } from "./MemberDetailSheet";

type ConfirmTarget =
	| {
			kind: "member";
			userId: Id<"users">;
			name: string;
	  }
	| {
			kind: "preRegistered";
			id: Id<"preRegisteredUsers">;
			email: string;
	  }
	| {
			kind: "domain";
			id: Id<"tenantDomains">;
			domain: string;
	  };

export default function FirmDetailPage() {
	const navigate = useNavigate();
	const params = useParams<{ tenantId: string }>();
	if (!params.tenantId) throw new Error("Missing tenantId route param");
	// URL params are the external boundary — Convex validates the Id server-side.
	const tenantId = params.tenantId as Id<"tenants">;

	const firms = useQuery(api.staff.listAllFirms);
	const firm = firms?.find((f) => f.tenantId === tenantId);
	const members = useQuery(api.staff.listFirmMembers, { tenantId });
	const domains = useQuery(api.staff.listTenantDomains, { tenantId });

	const removeFirmMember = useMutation(api.staff.removeFirmMember);
	const removePreRegisteredUser = useMutation(
		api.staff.removePreRegisteredUser,
	);
	const removeTenantDomain = useMutation(api.staff.removeTenantDomain);
	const switchToFirm = useMutation(api.staff.switchToFirm);

	const [addUserOpen, setAddUserOpen] = useState(false);
	const [addDomainOpen, setAddDomainOpen] = useState(false);
	const [selectedMemberId, setSelectedMemberId] =
		useState<Id<"tenantMembers"> | null>(null);
	const [confirmTarget, setConfirmTarget] = useState<ConfirmTarget | null>(
		null,
	);
	const [confirmError, setConfirmError] = useState<string | null>(null);

	const selectedMember: MemberDetail | null =
		members?.active.find((m) => m.membershipId === selectedMemberId) ?? null;

	const { mutate: doSwitch, isPending: switching } = useReactQueryMutation({
		mutationFn: async () => {
			const { authOrgId } = await switchToFirm({ tenantId });
			const { authClient } = await import("../../lib/authClient");
			await authClient.organization.setActive({ organizationId: authOrgId });
			navigate(0);
		},
	});

	const { mutate: runConfirm, isPending: confirming } = useReactQueryMutation({
		mutationFn: async (target: ConfirmTarget) => {
			if (target.kind === "member") {
				await removeFirmMember({ tenantId, userId: target.userId });
			} else if (target.kind === "preRegistered") {
				await removePreRegisteredUser({ id: target.id });
			} else {
				await removeTenantDomain({ id: target.id });
			}
		},
		onSuccess: () => {
			setConfirmTarget(null);
			setConfirmError(null);
		},
		onError: (err) => {
			setConfirmError(err instanceof Error ? err.message : String(err));
		},
	});

	return (
		<div className="flex flex-col gap-4">
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-3">
					<Link
						to="/staff/firms"
						className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
					>
						<ChevronLeftIcon className="size-4" />
						All firms
					</Link>
				</div>
				<Button
					variant="outline"
					onClick={() => doSwitch()}
					disabled={switching || !firm}
				>
					{switching ? "Switching..." : "Open workspace"}
				</Button>
			</div>

			<Card>
				<CardHeader>
					<CardTitle>{firm ? firm.name : "Loading..."}</CardTitle>
				</CardHeader>
				<CardContent className="grid grid-cols-2 gap-3 text-sm text-muted-foreground sm:grid-cols-4">
					<div>
						<div className="text-xs uppercase tracking-wide">Slug</div>
						<div className="text-foreground">{firm?.slug ?? "—"}</div>
					</div>
					<div>
						<div className="text-xs uppercase tracking-wide">Members</div>
						<div className="text-foreground">{firm?.memberCount ?? 0}</div>
					</div>
					<div>
						<div className="text-xs uppercase tracking-wide">Pending</div>
						<div className="text-foreground">{firm?.pendingUserCount ?? 0}</div>
					</div>
					<div>
						<div className="text-xs uppercase tracking-wide">Codex</div>
						<div className="capitalize text-foreground">
							{firm?.codexStatus?.replace("_", " ") ?? "—"}
						</div>
					</div>
				</CardContent>
			</Card>

			{firm ? (
				<FirmNoticeFilingCard
					tenantId={tenantId}
					scannerSenderAddress={firm.scannerSenderAddress}
				/>
			) : null}

			{firm ? (
				<FirmOnboardingFeeCard
					tenantId={tenantId}
					currentFeeCents={firm.onboardingFeePerSeatCents}
				/>
			) : null}

			<Card>
				<CardHeader className="flex flex-row items-center justify-between">
					<CardTitle>Allowed email domains</CardTitle>
					<Button size="sm" onClick={() => setAddDomainOpen(true)}>
						<PlusIcon data-icon="inline-start" />
						Add domain
					</Button>
				</CardHeader>
				<CardContent>
					<p className="mb-3 text-sm text-muted-foreground">
						When someone signs in with an email at one of these domains and has
						no pre-registered record, they're auto-added to this firm as a
						member. Pre-registered users still take precedence. Personal domains
						like gmail.com aren't allowed.
					</p>
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Domain</TableHead>
								<TableHead>Added</TableHead>
								<TableHead />
							</TableRow>
						</TableHeader>
						<TableBody>
							{domains === undefined ? (
								<TableRow>
									<TableCell colSpan={3} className="text-muted-foreground">
										Loading...
									</TableCell>
								</TableRow>
							) : domains.length === 0 ? (
								<TableRow>
									<TableCell colSpan={3} className="text-muted-foreground">
										No domains configured. Add one to auto-enroll users who sign
										in with a matching email.
									</TableCell>
								</TableRow>
							) : (
								domains.map((d) => (
									<TableRow key={d.id}>
										<TableCell className="font-medium">{d.domain}</TableCell>
										<TableCell className="text-muted-foreground">
											{new Date(d.createdAt).toLocaleDateString()}
										</TableCell>
										<TableCell className="text-right">
											<Button
												variant="ghost"
												size="sm"
												onClick={() => {
													setConfirmError(null);
													setConfirmTarget({
														kind: "domain",
														id: d.id,
														domain: d.domain,
													});
												}}
											>
												Remove
											</Button>
										</TableCell>
									</TableRow>
								))
							)}
						</TableBody>
					</Table>
				</CardContent>
			</Card>

			<Card>
				<CardHeader className="flex flex-row items-center justify-between">
					<CardTitle>Members</CardTitle>
				</CardHeader>
				<CardContent>
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Name</TableHead>
								<TableHead>Email</TableHead>
								<TableHead>Joined</TableHead>
								<TableHead>Auto-draft</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{members === undefined ? (
								<TableRow>
									<TableCell colSpan={4} className="text-muted-foreground">
										Loading...
									</TableCell>
								</TableRow>
							) : members.active.length === 0 ? (
								<TableRow>
									<TableCell colSpan={4} className="text-muted-foreground">
										No members yet.
									</TableCell>
								</TableRow>
							) : (
								members.active.map((m) => (
									<TableRow
										key={m.membershipId}
										className="cursor-pointer"
										onClick={() => setSelectedMemberId(m.membershipId)}
									>
										<TableCell>
											<span className="font-medium">{m.name}</span>
											{m.isStaff ? (
												<span className="ml-2 inline-block rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
													Staff
												</span>
											) : null}
										</TableCell>
										<TableCell className="text-muted-foreground">
											{m.email}
										</TableCell>
										<TableCell className="text-muted-foreground">
											{new Date(m.joinedAt).toLocaleDateString()}
										</TableCell>
										<TableCell className="text-muted-foreground">
											{m.outlook
												? m.outlook.autoDraftEnabled
													? "On"
													: "Off"
												: "—"}
										</TableCell>
									</TableRow>
								))
							)}
						</TableBody>
					</Table>
				</CardContent>
			</Card>

			<Card>
				<CardHeader className="flex flex-row items-center justify-between">
					<CardTitle>Pre-registered</CardTitle>
					<Button size="sm" onClick={() => setAddUserOpen(true)}>
						<PlusIcon data-icon="inline-start" />
						Add user
					</Button>
				</CardHeader>
				<CardContent>
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Name</TableHead>
								<TableHead>Email</TableHead>
								<TableHead>Added</TableHead>
								<TableHead />
							</TableRow>
						</TableHeader>
						<TableBody>
							{members === undefined ? (
								<TableRow>
									<TableCell colSpan={4} className="text-muted-foreground">
										Loading...
									</TableCell>
								</TableRow>
							) : members.preRegistered.length === 0 ? (
								<TableRow>
									<TableCell colSpan={4} className="text-muted-foreground">
										No pending pre-registrations.
									</TableCell>
								</TableRow>
							) : (
								members.preRegistered.map((p) => (
									<TableRow key={p.id}>
										<TableCell className="font-medium">
											{p.firstName} {p.lastName}
										</TableCell>
										<TableCell className="text-muted-foreground">
											{p.email}
										</TableCell>
										<TableCell className="text-muted-foreground">
											{new Date(p.createdAt).toLocaleDateString()}
										</TableCell>
										<TableCell className="text-right">
											<Button
												variant="ghost"
												size="sm"
												onClick={() => {
													setConfirmError(null);
													setConfirmTarget({
														kind: "preRegistered",
														id: p.id,
														email: p.email,
													});
												}}
											>
												Remove
											</Button>
										</TableCell>
									</TableRow>
								))
							)}
						</TableBody>
					</Table>
				</CardContent>
			</Card>

			<AddPreRegisteredUserDialog
				tenantId={tenantId}
				open={addUserOpen}
				onOpenChange={setAddUserOpen}
			/>

			<AddTenantDomainDialog
				tenantId={tenantId}
				open={addDomainOpen}
				onOpenChange={setAddDomainOpen}
			/>

			<MemberDetailSheet
				tenantId={tenantId}
				member={selectedMember}
				open={!!selectedMemberId}
				onOpenChange={(open) => {
					if (!open) setSelectedMemberId(null);
				}}
				onRequestRemove={(m) => {
					setConfirmError(null);
					setConfirmTarget({
						kind: "member",
						userId: m.userId,
						name: m.name,
					});
					setSelectedMemberId(null);
				}}
			/>

			<AlertDialog
				open={!!confirmTarget}
				onOpenChange={(open) => {
					if (!open && !confirming) {
						setConfirmTarget(null);
						setConfirmError(null);
					}
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>
							{confirmTarget?.kind === "member"
								? "Remove member?"
								: confirmTarget?.kind === "preRegistered"
									? "Remove pre-registered user?"
									: "Remove domain?"}
						</AlertDialogTitle>
						<AlertDialogDescription>
							{confirmTarget?.kind === "member" ? (
								<>
									Remove <strong>{confirmTarget.name}</strong> from this firm.
									They will lose access immediately; their account stays.
								</>
							) : confirmTarget?.kind === "preRegistered" ? (
								<>
									Delete the pending pre-registration for{" "}
									<strong>{confirmTarget.email}</strong>. They won't be able to
									sign in to this firm unless re-added.
								</>
							) : confirmTarget?.kind === "domain" ? (
								<>
									Remove <strong>{confirmTarget.domain}</strong> from this firm.
									New users at this domain will no longer be auto-enrolled.
								</>
							) : null}
						</AlertDialogDescription>
					</AlertDialogHeader>
					{confirmError ? (
						<p className="text-sm text-destructive">{confirmError}</p>
					) : null}
					<AlertDialogFooter>
						<AlertDialogCancel disabled={confirming}>Cancel</AlertDialogCancel>
						<AlertDialogAction
							variant="destructive"
							disabled={confirming}
							onClick={(e) => {
								e.preventDefault();
								if (!confirmTarget) return;
								runConfirm(confirmTarget);
							}}
						>
							{confirming ? "Removing..." : "Remove"}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}
